/**
 * useEdgeStatus: the signed-in user's proven-edge status for the strategy score badge (backlog
 * 240). Every score panel asks for it, so one request per user per five minutes is shared.
 */

import { useEffect, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import { type EdgeStatusView, readEdgeStatus } from '../edgeStatus';

const STALE_MS = 5 * 60 * 1000;

let cached: { userId: string; fetchedAt: number; status: Promise<EdgeStatusView | null> } | null =
  null;

/** The cached status for this user, or a new request; a failed request is not kept. */
export function loadEdgeStatus(
  httpClient: HttpClient,
  userId: string,
  now: number = Date.now(),
): Promise<EdgeStatusView | null> {
  if (cached && cached.userId === userId && now - cached.fetchedAt < STALE_MS) {
    return cached.status;
  }
  const status: Promise<EdgeStatusView | null> = httpClient
    .get<unknown>('/api/scoring/edge-status')
    .then(readEdgeStatus)
    .catch(() => {
      if (cached?.status === status) cached = null;
      return null;
    });
  cached = { userId, fetchedAt: now, status };
  return status;
}

export interface UseEdgeStatusOptions {
  httpClient: HttpClient;
  /** Null when signed out: there is no status to show. */
  userId: string | null;
}

/** Null while loading, on an error, or signed out, so the badge slot stays empty. */
export function useEdgeStatus({ httpClient, userId }: UseEdgeStatusOptions): EdgeStatusView | null {
  const [status, setStatus] = useState<EdgeStatusView | null>(null);

  useEffect(() => {
    if (!userId) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    loadEdgeStatus(httpClient, userId).then((loaded) => {
      if (!cancelled) setStatus(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [httpClient, userId]);

  return status;
}
